import ProductListQuery from 'Query/ProductList.query';
import { updateLinkedProducts } from 'Store/LinkedProducts/LinkedProducts.action';
import { showNotification } from 'Store/Notification/Notification.action';
import BrowserDatabase from 'Util/BrowserDatabase';
import componentLoader from 'Util/componentLoader';
import { getIndexedProduct } from 'Util/Product';
import PRODUCT_TYPE from 'Util/Product/Product.config';
import { fetchQuery, QueryDispatcher } from 'Util/Request';
import { ONE_MONTH_IN_SECONDS } from 'Util/Request/QueryDispatcher';
export const LINKED_PRODUCTS = 'LINKED_PRODUCTS';

export const LinkedProductsPriceDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/LinkedProductsPrice/LinkedProductsPrice.dispatcher'
), 2);
export const LinkedProductsVariantsDispatcher = componentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/LinkedProductsVariants/LinkedProductsVariants.dispatcher'
), 2);

/** @namespace Bodypwa/Store/LinkedProducts/Dispatcher */
export class LinkedProductsDispatcher extends QueryDispatcher {
    constructor() {
        super('LinkedProducts', ONE_MONTH_IN_SECONDS);
    }

    async fetchCrossSellProducts(dispatch, product_links) {
        const query = this.prepareRequest(product_links);
        const data = await fetchQuery(query);
        const linkedProducts = BrowserDatabase.getItem(LINKED_PRODUCTS) || {};
        const { products: { items = [] } = {} } = data;
        const itemsWithPrice = [];
        const itemsWithVariants = [];

        items.filter((item) => {
            const { attributes } = item;
            const filteredAttributes = attributes ? attributes.filter((attr) => item.type_id
            === PRODUCT_TYPE.configurable && attr.attribute_options?.length > 1) : [];

            if (filteredAttributes.length) {
                itemsWithVariants.push(item.sku);
            }
            itemsWithPrice.push(item.sku);
            return item;
        });
        const { crosssell } = this._processResponse(data, product_links);

        Object.assign(linkedProducts, {
            crosssell,
            updateCrossSell: true
        });

        dispatch(updateLinkedProducts(linkedProducts));
        LinkedProductsVariantsDispatcher.then(({ default: dispatcher }) => {
            dispatcher.handleData(dispatch, {
                args: {
                    filter: { productsSkuArray: itemsWithVariants }
                },
                isPdp: false
            });
        });
        LinkedProductsPriceDispatcher.then(({ default: dispatcher }) => {
            dispatcher.handleData(dispatch, {
                args: { filter: { productsSkuArray: itemsWithPrice } },
                isPdp: false
            });
        });
    }

    onSuccess(data, dispatch, product_links) {
        const { products: { items = [] } = {} } = data;
        const itemsWithPrice = [];
        const itemsWithVariants = [];

        items.filter((item) => {
            const { attributes } = item;
            const filteredAttributes = attributes.filter((attr) => item.type_id
            === PRODUCT_TYPE.configurable && attr.attribute_options?.length > 1);

            if (filteredAttributes.length) {
                itemsWithVariants.push(item.sku);
            }
            itemsWithPrice.push(item.sku);
            return item;
        });

        const linkedProducts = this._processResponse(data, product_links);
        dispatch(updateLinkedProducts(linkedProducts));
        LinkedProductsVariantsDispatcher.then(({ default: dispatcher }) => {
            dispatcher.handleData(dispatch, {
                args: {
                    filter: { productsSkuArray: itemsWithVariants }
                },
                isPdp: false
            });
        });
        LinkedProductsPriceDispatcher.then(({ default: dispatcher }) => {
            dispatcher.handleData(dispatch, {
                args: { filter: { productsSkuArray: itemsWithPrice } },
                isPdp: false
            });
        });
    }

    onError(error, dispatch) {
        dispatch(showNotification('error', __('Error fetching LinkedProducts!'), error));
    }

    /**
     * Prepare LinkedProducts query
     * @return {Query} ProductList query
     * @memberof LinkedProductsDispatcher
     * @param product_links
     */
    prepareRequest(product_links) {
        const relatedSKUs = product_links.reduce((links, link) => {
            const { linked_product_sku } = link;

            return [...links, `${ linked_product_sku.replace(/ /g, '%20') }`];
        }, []);

        return [
            ProductListQuery.getQuery({
                args: {
                    filter: {
                        productsSkuArray: relatedSKUs
                    }
                },
                notRequireInfo: true
            })
        ];
    }

    /**
     * Clear linked products list
     * @param {{productsSkuArray: Array<String>}} options A object containing different aspects of query, each item can be omitted
     * @return {Query} ProductList query
     * @memberof LinkedProductsDispatcher
     */
    clearLinkedProducts(dispatch, updateCrossSell = false) {
        const linkedProducts = {
            upsell: { total_count: 0, items: [] },
            related: { total_count: 0, items: [] },
            crosssell: { total_count: 0, items: [] }
        };

        BrowserDatabase.setItem(linkedProducts, LINKED_PRODUCTS);

        dispatch(updateLinkedProducts({
            ...linkedProducts,
            updateCrossSell
        }));
    }

    clearCrossSellProducts(dispatch) {
        const linkedProducts = BrowserDatabase.getItem(LINKED_PRODUCTS) || {};

        Object.assign(linkedProducts, {
            crosssell: { total_count: 0, items: [] },
            updateCrossSell: true
        });

        dispatch(updateLinkedProducts(linkedProducts));
    }

    _processResponse(data, product_links) {
        const { products: { items } } = data;

        const indexedBySku = items.reduce((acc, item) => {
            const { sku } = item;
            acc[sku] = getIndexedProduct(item);

            return acc;
        }, {});

        const linkedProducts = product_links.reduce((acc, link) => {
            const { linked_product_sku, link_type } = link;

            if (indexedBySku[linked_product_sku] && acc[link_type]) {
                acc[link_type].items.push(
                    indexedBySku[linked_product_sku]
                );

                acc[link_type].total_count++;
            }

            return acc;
        }, {
            upsell: { total_count: 0, items: [] },
            related: { total_count: 0, items: [] },
            crosssell: { total_count: 0, items: [] },
            associated: { total_count: 0, items: [] }
        });

        return linkedProducts;
    }
}

export default new LinkedProductsDispatcher();
